<template>
  <div class="landing-page-v3">
    <!-- Navigation -->
    <PublicHeader />

    <!-- Main Content -->
    <main>
      <LandingHero />
      <LandingServices />
      <LandingProcess />
      <LandingPricing />
      <LandingFAQ />
      <LandingCTA />
    </main>

    <!-- Footer -->
    <PublicFooter />
  </div>
</template>

<script setup>
import { onMounted } from 'vue'
import { useSeo } from '@/composables/useSeo'
import { useStructuredData } from '@/composables/useStructuredData'
import { usePerformanceOptimization } from '@/composables/usePerformanceOptimization'
import { PRICING } from '@/utils/constants/pricing.js'
import PublicHeader from '@/components/layout/PublicHeader.vue'
import PublicFooter from '@/components/layout/PublicFooter.vue'
import LandingHero from '@/components/landing/LandingHero.vue'
import LandingServices from '@/components/landing/LandingServices.vue'
import LandingProcess from '@/components/landing/LandingProcess.vue'
import LandingPricing from '@/components/landing/LandingPricing.vue'
import LandingFAQ from '@/components/landing/LandingFAQ.vue'
import LandingCTA from '@/components/landing/LandingCTA.vue'

/**
 * LandingPage V3 - Storytelling Visual Design
 *
 * Migrated from public/prototypes/landing-v3-storytelling.html
 * Following MIGRATION_LANDING_V3.md guidelines
 *
 * New Design Features:
 * - Outfit (titles) + DM Sans (body) typography
 * - Floating pill header with glassmorphism
 * - Organic blob backgrounds
 * - Alternating service sections
 * - Timeline-based process
 * - Dark featured pricing plan
 * - Native accordion FAQ
 * - Gradient CTA section
 * - Dark footer with legal disclaimer
 *
 * Applies ADR-043: Vue Landing Components Architecture (Updated for V3)
 */

// Composables
const { updateMetaTags, setupLegalServiceSchema, injectStructuredData } = useStructuredData()
const { measurePerformance } = usePerformanceOptimization()

// SEO Meta Tags
useSeo({
  title: 'Derecho al Olvido y Testamento Digital',
  description: 'Ejercicio profesional del derecho al olvido RGPD. Eliminamos tu información de Internet de forma legal. Abogados especializados.',
  canonical: '/'
})

// SEO and meta tags setup
onMounted(() => {
  const endMeasurement = measurePerformance?.('component-mount', 'LandingPage')

  updateMetaTags({
    title: 'Olvidadme - Protege tu Privacidad Digital | Derecho al Olvido',
    description: 'Ejercicio profesional del derecho al olvido conforme al RGPD. Eliminamos tu información personal de Internet de forma legal. Desde 499€.',
    keywords: 'derecho al olvido, GDPR, RGPD, privacidad digital, eliminar datos internet, artículo 17'
  })

  const structuredData = setupLegalServiceSchema({
    hasOfferCatalog: {
      "@type": "OfferCatalog",
      "name": "Servicios de Privacidad Digital",
      "itemListElement": [
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": PRICING.OLVIDO.name,
            "description": "Eliminación de datos personales de internet para personas vivas según GDPR Art. 17"
          },
          "price": String(PRICING.OLVIDO.price),
          "priceCurrency": "EUR"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": PRICING.OLVIDO_POSTMORTEM.name,
            "description": "Eliminación de datos personales de buscadores y plataformas de personas fallecidas"
          },
          "price": String(PRICING.OLVIDO_POSTMORTEM.price),
          "priceCurrency": "EUR"
        },
        {
          "@type": "Offer",
          "itemOffered": {
            "@type": "Service",
            "name": PRICING.TESTAMENTO.name,
            "description": "Planificación del legado digital con instrucciones para albacea"
          },
          "price": String(PRICING.TESTAMENTO.price),
          "priceCurrency": "EUR"
        }
      ]
    }
  })

  injectStructuredData(structuredData)
  endMeasurement?.()
})
</script>

<style scoped>
@reference "../assets/styles/main.css";

.landing-page-v3 {
  min-height: 100vh;
  background-color: #f9fafb;
}

/* Typography system - already handled by main.css base styles */

/* Smooth scrolling */
html {
  scroll-behavior: smooth;
}

/* Reduced motion support */
@media (prefers-reduced-motion: reduce) {
  .landing-page-v3 *,
  .landing-page-v3 *::before,
  .landing-page-v3 *::after {
    animation-duration: 0.01ms !important;
    animation-iteration-count: 1 !important;
    transition-duration: 0.01ms !important;
    scroll-behavior: auto !important;
  }
}

/* Print styles */
@media print {
  .landing-page-v3 {
    background: white !important;
  }
}
</style>
